import * as Sentry from '@sentry/nextjs';
import { routeTemplate } from './server-error-watch';
import { reloadsHeld } from '@/lib/reload-guard';
import { notePageLeaving } from '@/lib/page-leaving';

/**
 * Report a client-side navigation that was asked for and never arrived.
 *
 * ── Why this exists ────────────────────────────────────────────────────────
 *
 * MUCKA-WEB-7Y (30 Sep 2026): a plumbing firm's engineer tapped "Draft a Warning
 * Notice" twice, 35 seconds apart. Both notices were created and both pages'
 * RSC payloads arrived within a second, but the screen never moved. A minute
 * later, typing into a comments box threw "Maximum update depth exceeded"
 * about every eleven seconds. That is React's nested-update counter (more than
 * 50 commits in a row that each left urgent work queued) being tripped by
 * ordinary keystrokes, so the page was already wedged before he typed a word.
 * What wedged it is still unknown. The one signal it gave was the error, and
 * the error only came because he happened to type afterwards. A stuck page
 * the user simply gives up on reports nothing.
 *
 * So watch for the stuck navigation itself, with enough state to say which
 * step it stuck on next time.
 *
 * ── How "never arrived" is decided ─────────────────────────────────────────
 *
 * The App Router writes the new URL into history in an insertion effect, i.e.
 * when the new page COMMITS. So `location` still reading the page we left is
 * proof the navigation has not committed, and `location` reading anything
 * else (the target, a redirect, a page the user went to instead) means it is
 * not stuck.
 *
 * 🚨 The false positive to design against is a slow network: a page that takes
 * 15 seconds to arrive is slow, not stuck, and a detector that reports it
 * teaches everyone to ignore the issue. So elapsed time alone never reports.
 * The report needs the route's RSC payload to have ALREADY ARRIVED
 * (`performance` resource timing for a `?_rsc=` request to the target), and
 * the page still not to have moved a full `STUCK_AFTER_MS` after that. Data
 * in hand plus no commit is not the network. With no payload in sight it keeps
 * waiting, up to `GIVE_UP_AFTER_MS`, and then stops without reporting.
 *
 * Two more ways to be wrong, both handled:
 * - A phone that locked mid-navigation freezes timers AND rendering, so on
 *   wake the timer can run before React finishes. A check that fires late,
 *   or while the page is hidden, re-arms instead of judging.
 * - A newer navigation replaces the older one. Only the latest is watched.
 *
 * ── The first three reports, and the two ways they were wrong ──────────────
 *
 * MUCKA-WEB-BA's first three events (30 Sep – 1 Oct 2026) included two that
 * were not stuck at all, and each one broke a premise above.
 *
 * 1. "`location` still reading the page we left proves no commit" is false
 *    when the page we left and the page we went to share a URL once tidied.
 *    `/jobs` → `/jobs?open=<id>` COMMITTED, and 274ms later `useJobDeepLink`
 *    consumed the link with `history.replaceState(…, '/jobs')`. `location`
 *    read `/jobs` again, i.e. the page we left, and a navigation that had
 *    arrived was reported as one that never did. Any consume-and-strip query
 *    param has this shape. So `location` is not the only witness any more:
 *    `history.state` is captured when the navigation starts, and the App
 *    Router writes a NEW state object on every commit (and its patched
 *    `replaceState` wraps every external one), so a different object means
 *    history was written, which a stuck navigation never does.
 *
 * 2. "The payload has arrived" matched a resource-timing entry from BEFORE
 *    the tap. A WebView page lives for days and the buffer keeps every
 *    `?_rsc=` fetch it ever made, so an old visit to `/schedule` read as this
 *    visit's data being in hand. The tell in the event was `payloadReadyMs`
 *    equal to `waitedMs` (the clamp to `startedAt`). That report's navigation
 *    then ARRIVED, as a full page load of a newer build 4.8 seconds later,
 *    which is what Next does when an RSC response comes from a different
 *    deployment. Only a payload that finished arriving AFTER the navigation
 *    began counts now.
 *
 * ── Stuck, or only slow to draw? ───────────────────────────────────────────
 *
 * The two events after #3110 (2 Oct 2026, both the Android app) passed every
 * test above and still could not say which they were. The page's data was in
 * hand and the page had not moved for ten seconds, but React renders a
 * navigation in time slices, so a page that takes twenty seconds to draw on a
 * budget phone leaves the timers firing on time and reads exactly like one
 * that will never draw. And the report went at the ten-second mark, so
 * nobody ever learnt which.
 *
 * So the ten-second mark no longer reports. It makes the navigation a
 * SUSPECT, and the watch carries on until the page moves (`arrived_late`,
 * with how long it took), the user taps somewhere else (`superseded`), the
 * app is put away (`left`, sent at once because a backgrounded WebView may
 * never run again), or the minute is up (`never_arrived`). Every report
 * carries the outcome as a tag, so one issue splits into "slow" and "stuck"
 * by a query rather than by a guess. Alongside it, what the page was doing
 * meanwhile: how late our own timers fired (a busy main thread), and when a
 * script last finished arriving after the tap (a lazy chunk the new page was
 * waiting on, which the RSC payload test cannot see).
 *
 * ── A replay of the wedge itself ───────────────────────────────────────────
 *
 * Every report above says WHAT state the page was in; none can show what the
 * person was looking at or tapping. Eight reports from 8 to 12 Oct 2026
 * (MUCKA-WEB-C5, CQ) had the data in hand, nothing loading and an idle main
 * thread, which rules out every theory the fields were added to test, and not
 * one carried a replay. Replays are buffered for every session
 * (`replaysOnErrorSampleRate: 1`), but the SDK flushes the buffer only for an
 * event with an EXCEPTION (`shouldSampleForBufferEvent` in
 * `@sentry/replay`), and this report is a `captureMessage`. So the replay was
 * recorded on every one of those phones and thrown away each time.
 *
 * So a report of a page that would not move (`superseded`, `never_arrived`)
 * tags the buffered replay's id and flushes it. Not `arrived_late` (it drew)
 * and not `left` (the app is being put away, and the upload would rarely
 * finish). The plan includes 50 replays a month and the two outcomes together
 * have run at about one a day, so this is worth reading against the replay
 * quota after a fortnight.
 *
 * ── Cost ───────────────────────────────────────────────────────────────────
 *
 * No polling: `onRouterTransitionStart` arms one timer per navigation, a
 * check reads `location` and the resource-timing buffer once, and it re-arms
 * at most a handful of times before giving up. Only a suspect re-checks every
 * two seconds and holds a `visibilitychange` listener, for at most a minute.
 * One report per page load.
 */

export const STUCK_AFTER_MS = 10_000;
export const GIVE_UP_AFTER_MS = 60_000;
/** How often a suspect is re-checked for having arrived after all. */
const SUSPECT_RECHECK_MS = 2_000;
/** A check this much later than scheduled was frozen, not merely late. */
const FROZEN_SLACK_MS = 2_000;

export const STUCK_NAV_MESSAGE = 'Client navigation requested but never arrived';

type NavigationType = 'push' | 'replace' | 'traverse';

/** How a suspect navigation ended. The `outcome` tag on every report. */
export type StuckNavOutcome = 'arrived_late' | 'superseded' | 'left' | 'never_arrived';

interface Pending {
  from: string;
  /**
   * `history.state` when the navigation started. A commit (or anything else
   * that writes history) replaces it, so a different object means the page
   * moved even if `location` has since been tidied back to `from`.
   */
  historyState: unknown;
  to: string;
  toPath: string;
  type: NavigationType;
  startedAt: number;
  timer: ReturnType<typeof setTimeout>;
  /** Worst lateness of any check so far: a busy main thread fires timers late. */
  maxTimerLagMs: number;
  /**
   * Set once the data has been in hand for `STUCK_AFTER_MS` with no commit,
   * with what was still loading at that moment (see `awaitedAssets`).
   */
  suspect: { at: number; readyFor: number; assets: AssetsInFlight } | null;
}

let pending: Pending | null = null;
let reported = false;
/** Navigations started while one was already pending: a stuck page gets re-tapped. */
let supersededWhilePending = 0;

function here(): string {
  return `${window.location.pathname}${window.location.search}`;
}

function historyState(): unknown {
  try {
    return window.history.state;
  } catch {
    return undefined;
  }
}

/**
 * Has anything happened since the navigation started that a stuck page never
 * does? The URL moving, or history being written at all. See "the first three
 * reports" above for the commit that put `location` straight back.
 */
function moved(p: Pending): boolean {
  return here() !== p.from || historyState() !== p.historyState;
}

/**
 * When the target route's RSC payload finished arriving (a `performance.now()`
 * timestamp), or null if it has not arrived since `since`.
 *
 * 🚨 Only a payload that finished AFTER the navigation began. The resource
 * buffer holds every `?_rsc=` fetch the page has made, and on a WebView that
 * has been open since breakfast an old visit to the same route is nearly
 * always in it. That old entry says nothing about whether THIS navigation's
 * data is here (Next refetches dynamic pages), and counting it reported a
 * navigation still on the network as stuck. A prefetch that was in flight
 * when the user tapped and lands afterwards still counts.
 */
export function payloadArrivedAt(toPath: string, since: number): number | null {
  let latest: number | null = null;
  try {
    for (const entry of performance.getEntriesByType('resource')) {
      if (!entry.name.includes('_rsc=')) continue;
      let path: string;
      try {
        path = new URL(entry.name, window.location.href).pathname;
      } catch {
        continue;
      }
      if (path !== toPath) continue;
      const end = (entry as PerformanceResourceTiming).responseEnd;
      if (end < since) continue;
      if (end > 0 && (latest === null || end > latest)) latest = end;
    }
  } catch {
    return null;
  }
  return latest;
}

function arm(p: Pending, delay: number): void {
  const due = performance.now() + delay;
  p.timer = setTimeout(() => check(p, due), delay);
}

function check(p: Pending, due: number): void {
  if (pending !== p) return;
  const now = performance.now();

  if (p.suspect) {
    // A lock or a background fires `visibilitychange` first, so a late check
    // here is the main thread, not a frozen page.
    p.maxTimerLagMs = Math.max(p.maxTimerLagMs, now - due);
    if (moved(p)) settle(p, now, 'arrived_late');
    else if (now - p.startedAt > GIVE_UP_AFTER_MS) settle(p, now, 'never_arrived');
    else arm(p, SUSPECT_RECHECK_MS);
    return;
  }

  // Anywhere but where we started means it committed, redirected, or the user
  // went somewhere else. None of those is a stuck page. Nor is a page whose
  // history was written and then tidied back to the same URL.
  if (moved(p)) {
    pending = null;
    return;
  }

  if (now - p.startedAt > GIVE_UP_AFTER_MS) {
    pending = null;
    return;
  }

  // Frozen (locked phone, background tab) or hidden: rendering was paused too,
  // so nothing can be concluded yet.
  if (document.visibilityState !== 'visible' || now - due > FROZEN_SLACK_MS) {
    arm(p, STUCK_AFTER_MS);
    return;
  }
  p.maxTimerLagMs = Math.max(p.maxTimerLagMs, now - due);

  const arrived = payloadArrivedAt(p.toPath, p.startedAt);
  if (arrived === null) {
    // Still on the network, or we cannot tell. Slow is not stuck.
    arm(p, STUCK_AFTER_MS);
    return;
  }

  const readyFor = now - arrived;
  if (readyFor < STUCK_AFTER_MS) {
    arm(p, STUCK_AFTER_MS - readyFor);
    return;
  }

  // Data in hand for ten seconds and no commit. Not reported yet: wait to see
  // whether it is stuck or only slow to draw (see "Stuck, or only slow").
  p.suspect = { at: now, readyFor, assets: assetsInFlight() };

  // Unless it is a wedge we can get them out of (see `canRecover`).
  if (canRecover(p.suspect.assets) && !reloadsHeld() && claimRecovery()) {
    recover(p, now);
    return;
  }
  if (!removeHiddenListener) {
    document.addEventListener('visibilitychange', onHidden);
    removeHiddenListener = () => document.removeEventListener('visibilitychange', onHidden);
  }
  arm(p, SUSPECT_RECHECK_MS);
}

let removeHiddenListener: (() => void) | null = null;

/**
 * Is a script of ours still loading ten seconds after the page's data arrived?
 *
 * MUCKA-WEB-C5 is why this exists. Turbopack caches each chunk's load as one
 * promise that settles only when the script runs, so a request that never
 * finishes (iOS cutting one off when the app is put away, or a connection
 * that stalls) leaves every later navigation to that page waiting on the same
 * promise for ever. Re-tapping cannot help, because it waits on the same
 * promise. The first report after #3332 (10 Oct 2026, a bathroom firm on
 * Windows Chrome) is that case: data in hand, three `.js` chunks still in
 * flight at ten seconds, nothing loaded after the tap, and a re-tap.
 *
 * A stylesheet in flight is NOT enough on its own: React holds a commit for
 * one, and a slow one is not a dead one. An unknown answer (a full resource
 * buffer) is not enough either.
 */
export function stuckOnAScript(assets: { paths: string[] | null }): boolean {
  return !!assets.paths?.some((path) => path.endsWith('.js'));
}

/**
 * Is this a wedge a full page load gets them out of?
 *
 * A script still loading (above), and also NOTHING loading at all. The second
 * is the commoner case and #3452 left it out because it could not name a
 * cause: the data has been here for ten seconds, no script or stylesheet of
 * ours is outstanding, the main thread is idle, and React simply never drew
 * the page. What the data showed by 15 Oct 2026, across MUCKA-WEB-C5 and CQ:
 * all eight reports tagged `assets_in_flight: no` were genuine wedges (the
 * person re-tapped, or gave up and put the app away after 12 to 20 seconds),
 * and both reports that DID draw late (`arrived_late`, MUCKA-WEB-CB) had a
 * chunk still loading. So with nothing loading, ten seconds of waiting has
 * never once ended in the page drawing. Cause still unknown — a recovery is
 * logged, not raised, so how often it happens can still be counted.
 *
 * NOT a stylesheet alone (React holds a commit for one and a slow one is not a
 * dead one), and NOT unknown (a full resource buffer could be hiding either).
 */
export function canRecover(assets: { paths: string[] | null }): boolean {
  if (stuckOnAScript(assets)) return true;
  return Array.isArray(assets.paths) && assets.paths.length === 0;
}

/** Two minutes. A recovery that lands on the same wedge must not loop. */
const RECOVERY_COOLDOWN_MS = 2 * 60_000;
const RECOVERY_KEY = 'mucka:stuck-nav-recovered-at';

/**
 * One full-page recovery per two minutes per tab. `sessionStorage` is the
 * right store here even though iOS evicts it: the only job is to stop a loop
 * within one tab's life, and an evicted tab has already started again. A
 * storage that throws allows the recovery: the cost of one extra reload is
 * small next to a page that will not move.
 */
function claimRecovery(): boolean {
  try {
    const last = Number(window.sessionStorage.getItem(RECOVERY_KEY) || 0);
    if (Date.now() - last < RECOVERY_COOLDOWN_MS) return false;
    window.sessionStorage.setItem(RECOVERY_KEY, String(Date.now()));
  } catch {
    // Storage unavailable: allow it.
  }
  return true;
}

/**
 * Get the user to where they tapped with a full page load, which starts a
 * fresh JavaScript runtime and so a fresh chunk cache.
 *
 * A recovery is not an issue (Tom, 10 Oct 2026: report failures, not
 * rescues). It goes to Sentry's structured logs, which are searchable and
 * raise nothing, so how often it happens can still be counted. A wedge we
 * could NOT recover from (a reload hold, the cooldown, a stylesheet or an
 * unknown in flight) still reports through `settle` as before.
 *
 * The reload hold is checked by the caller: a full page load in the middle of
 * a voice call hangs up on the customer (`reload-guard.ts`), and a stuck page
 * is the lesser harm.
 */
function recover(p: Pending, now: number): void {
  if (pending === p) pending = null;
  clearTimeout(p.timer);
  const toRoute = routeTemplate(p.toPath);
  try {
    Sentry.logger.warn('[stuck-navigation] recovered with a full page load', {
      fromRoute: routeTemplate(new URL(p.from, window.location.href).pathname),
      toRoute,
      payloadReadyMs: Math.round(p.suspect?.readyFor ?? 0),
      waitedMs: Math.round(now - p.startedAt),
      assetsInFlightNames: p.suspect?.assets.names?.join(',') ?? '',
      // Which of the two wedges this was (see `canRecover`).
      stuckOn: stuckOnAScript(p.suspect?.assets ?? { paths: null }) ? 'script' : 'nothing',
      retappedWhilePending: supersededWhilePending,
    });
  } catch {
    // The recovery goes ahead whether or not the log does.
  }
  const why = stuckOnAScript(p.suspect?.assets ?? { paths: null }) ? 'stuck on a script' : 'nothing loading';
  console.warn(`[stuck-navigation] ${p.type} → ${toRoute}: ${why}, reloading`);
  // The script it was stuck on is cancelled by this load, and its failure
  // reaches the error boundary on the way out. Without this the boundary
  // reloaded on top of it (MUCKA-WEB-CV, 12 Oct 2026) — see `page-leaving.ts`.
  notePageLeaving('stuck-navigation');
  hardNavigate(p.to);
}

let hardNavigate = (url: string): void => window.location.assign(url);

/** Test seam: jsdom cannot navigate, so a test swaps in a spy. */
export function setHardNavigateForTests(fn: (url: string) => void): void {
  hardNavigate = fn;
}

/** The app was put away while a suspect was pending: say so now, it may not run again. */
function onHidden(): void {
  if (document.visibilityState !== 'hidden' || !pending?.suspect) return;
  clearTimeout(pending.timer);
  settle(pending, performance.now(), moved(pending) ? 'arrived_late' : 'left');
}

function settle(p: Pending, now: number, outcome: StuckNavOutcome): void {
  if (pending === p) pending = null;
  removeHiddenListener?.();
  removeHiddenListener = null;
  report(p, now, outcome);
}

function report(p: Pending, now: number, outcome: StuckNavOutcome): void {
  if (reported || !p.suspect) return;
  reported = true;
  const fromRoute = routeTemplate(new URL(p.from, window.location.href).pathname);
  const toRoute = routeTemplate(p.toPath);
  console.warn(`[stuck-navigation] ${p.type} ${fromRoute} → ${toRoute}: ${outcome} after ${Math.round((now - p.startedAt) / 1000)}s`);

  // Is a newer build already installed and waiting? Asked because an RSC
  // payload from a different deployment does not commit at all: Next abandons
  // the soft navigation and does a full page load instead, and on a poor
  // signal that load can take long enough to look exactly like this. A
  // waiting worker is the cheapest evidence the client is behind production.
  // Best-effort and bounded: the report goes either way.
  let sent = false;
  const send = (newerBuildWaiting: boolean | null) => {
    if (sent) return;
    sent = true;
    capture(p, now, outcome, fromRoute, toRoute, newerBuildWaiting);
  };
  try {
    const sw = typeof navigator !== 'undefined' ? navigator.serviceWorker : undefined;
    if (!sw?.getRegistration) {
      send(null);
      return;
    }
    setTimeout(() => send(null), 2_000);
    sw.getRegistration().then((r) => send(!!r?.waiting), () => send(null));
  } catch {
    send(null);
  }
}

/**
 * The last script to finish arriving after the navigation began, in ms from
 * the tap, and how many did. A new page's client components load as lazy
 * chunks AFTER its RSC payload, and a transition waiting on one cannot commit.
 */
function scriptsSince(since: number): { count: number; lastEndMs: number | null } {
  let count = 0;
  let last: number | null = null;
  try {
    for (const entry of performance.getEntriesByType('resource')) {
      const e = entry as PerformanceResourceTiming;
      if (e.initiatorType !== 'script' && !/\.js(\?|$)/.test(e.name)) continue;
      if (e.responseEnd < since) continue;
      count += 1;
      if (last === null || e.responseEnd > last) last = e.responseEnd;
    }
  } catch {
    // Unknown is fine; the report goes either way.
  }
  return { count, lastEndMs: last === null ? null : Math.round(last - since) };
}

/**
 * A browser stops recording resource timing at this many entries unless the
 * page enlarges or clears the buffer, which nothing in Mucka does. Past it, a
 * script that loaded perfectly well simply has no entry.
 */
const RESOURCE_BUFFER_DEFAULT = 250;
/** Enough names to identify the chunk without sending the whole page. */
const MAX_IN_FLIGHT_NAMES = 5;

interface AssetsInFlight {
  count: number | null;
  names: string[] | null;
  /** Every one, untruncated, so two snapshots can be compared. */
  paths: string[] | null;
  bufferFull: boolean;
}

/**
 * Our own scripts and stylesheets that are on the page but never finished
 * arriving, at any time — not only after the tap.
 *
 * MUCKA-WEB-C5 is why. A prefetch starts loading the target's chunks, and
 * Turbopack caches each chunk's load as one promise that settles only when the
 * script runs. A request iOS cut off when the app was put away can settle
 * neither way, and every later navigation to that page then waits on the same
 * promise for ever, fetching nothing (so `scriptsAfterTap` reads 0). React
 * also holds a commit for a stylesheet that has not loaded. Either leaves an
 * asset here; an empty list rules both out.
 *
 * A stylesheet answers for itself (`sheet` is set once it has loaded). A
 * script does not, so it counts as finished only if resource timing recorded
 * it, which is why a full buffer makes the answer unknown rather than "all of
 * them".
 */
function assetsInFlight(): AssetsInFlight {
  try {
    const resources = performance.getEntriesByType('resource') as PerformanceResourceTiming[];
    const bufferFull = resources.length >= RESOURCE_BUFFER_DEFAULT;
    const pathOf = (href: string): string | null => {
      try {
        const u = new URL(href, window.location.href);
        if (u.origin !== window.location.origin || !u.pathname.startsWith('/_next/static/')) return null;
        return u.pathname;
      } catch {
        return null;
      }
    };
    const done = new Set<string>();
    for (const e of resources) {
      if (e.responseEnd <= 0) continue;
      const path = pathOf(e.name);
      if (path) done.add(path);
    }

    const waiting = new Set<string>();
    let scriptUnknown = false;
    // `nomodule` is Next's polyfills script, which a browser that runs this
    // code never fetches, so it has no timing entry and would read as in flight
    // on every report (MUCKA-WEB-CB).
    for (const s of Array.from(document.querySelectorAll<HTMLScriptElement>('script[src]:not([nomodule])'))) {
      const path = pathOf(s.src);
      if (!path || done.has(path)) continue;
      if (bufferFull) scriptUnknown = true;
      else waiting.add(path);
    }
    for (const l of Array.from(document.querySelectorAll<HTMLLinkElement>('link[rel=stylesheet][href]'))) {
      const path = pathOf(l.href);
      if (path && !l.sheet) waiting.add(path);
    }

    if (scriptUnknown) return { count: null, names: null, paths: null, bufferFull };
    const paths = Array.from(waiting);
    return { count: paths.length, names: fileNames(paths), paths, bufferFull };
  } catch {
    return { count: null, names: null, paths: null, bufferFull: false };
  }
}

function fileNames(paths: string[]): string[] {
  return paths.slice(0, MAX_IN_FLIGHT_NAMES).map((p) => p.slice(p.lastIndexOf('/') + 1));
}

/**
 * The assets the navigation could have been waiting on, or null if unknown.
 *
 * For a page that never moved, that is whatever is still loading now. For one
 * that ARRIVED it is not: the commit happened, so whatever held it had settled
 * first, and an asset still loading afterwards is a lazy chunk the new page
 * never needed. MUCKA-WEB-CB's second report was exactly that — the native
 * voice engine's chunk, unfinished after /overview had drawn, tagged as the
 * thing the page waited on. So a late arrival names what was loading while it
 * was stuck AND had finished by the time it drew.
 */
function awaitedAssets(p: Pending, outcome: StuckNavOutcome, now: AssetsInFlight): string[] | null {
  if (outcome !== 'arrived_late') return now.paths;
  const before = p.suspect?.assets.paths;
  if (!before || !now.paths) return null;
  const still = new Set(now.paths);
  return before.filter((path) => !still.has(path));
}

function capture(
  p: Pending,
  now: number,
  outcome: StuckNavOutcome,
  fromRoute: string,
  toRoute: string,
  newerBuildWaiting: boolean | null,
): void {
  const scripts = scriptsSince(p.startedAt);
  const inFlight = assetsInFlight();
  const awaited = awaitedAssets(p, outcome, inFlight);
  const payload = payloadFetches(p.toPath, p.startedAt);
  const replayId = REPLAY_OUTCOMES.has(outcome) ? bufferedReplayId() : null;
  Sentry.captureMessage(STUCK_NAV_MESSAGE, {
    level: 'warning',
    // One issue PER OUTCOME. A shared fingerprint put fourteen-second slow
    // arrivals and an app put away mid-wait in the same issue as a page that
    // never moved, so the issue could never resolve and could never say which
    // it was holding (MUCKA-WEB-BA, 7 Oct 2026: the only two reports after
    // #3157 were `arrived_late` and `left`). `never_arrived` is the one that
    // means a stuck page; the others are separate questions.
    fingerprint: ['stuck-client-navigation', outcome],
    tags: {
      nav_type: p.type,
      from_route: fromRoute,
      to_route: toRoute,
      outcome,
      // A tag so "stuck on an asset" vs "stuck on something else" is a query.
      // What it waited on, not merely what is loading (see `awaitedAssets`).
      assets_in_flight: awaited === null ? 'unknown' : awaited.length > 0 ? 'yes' : 'no',
      // The tag Sentry links an event to its replay by (see "A replay").
      ...(replayId ? { replayId } : {}),
    },
    extra: {
      // Routes, not paths: an id in here would split one fault into one issue
      // per record (CLAUDE.md #13), and a query string can carry a name.
      fromRoute,
      toRoute,
      step: p.type,
      outcome,
      waitedMs: Math.round(now - p.startedAt),
      // When the data had been in hand ten seconds and the page had still not
      // committed it: the moment it became a suspect.
      suspectAtMs: Math.round(p.suspect!.at - p.startedAt),
      payloadReadyMs: Math.round(p.suspect!.readyFor),
      maxTimerLagMs: Math.round(p.maxTimerLagMs),
      scriptsAfterTap: scripts.count,
      lastScriptEndMs: scripts.lastEndMs,
      // File names only: content hashes, which map to a module in the build.
      assetsInFlight: inFlight.count,
      assetsInFlightNames: inFlight.names,
      assetsAwaitedNames: awaited === null ? null : fileNames(awaited),
      resourceBufferFull: inFlight.bufferFull,
      deviceMemoryGb: (navigator as Navigator & { deviceMemory?: number }).deviceMemory ?? null,
      cpuCores: navigator.hardwareConcurrency ?? null,
      retappedWhilePending: supersededWhilePending,
      online: typeof navigator !== 'undefined' ? navigator.onLine : null,
      swControlled: typeof navigator !== 'undefined' && !!navigator.serviceWorker?.controller,
      newerBuildWaiting,
      visibility: document.visibilityState,
      // How many times the target's data was fetched after the tap, and how
      // big the last one was. A re-tap refetches; a tiny body is a redirect
      // or a not-found rather than the page.
      payloadFetches: payload.count,
      payloadBytes: payload.lastBytes,
      // A sheet or dialog still open over the page we were leaving.
      overlayOpen: overlayOpen(),
      replayAttached: !!replayId,
    },
  });
  if (replayId) flushReplay();
}

/** The outcomes that mean a page that would not move. See "A replay". */
const REPLAY_OUTCOMES: ReadonlySet<StuckNavOutcome> = new Set(['superseded', 'never_arrived']);

/**
 * The id of the replay being recorded for this session, or null when there
 * is none. `getReplay` is absent where the integration is not installed, and
 * `flush()` on a replay that is not running STARTS one, so nothing is flushed
 * without an id first.
 */
function bufferedReplayId(): string | null {
  try {
    return Sentry.getReplay?.()?.getReplayId() ?? null;
  } catch {
    return null;
  }
}

/** Send the buffered replay. It carries on recording, as an error's would. */
function flushReplay(): void {
  try {
    void Sentry.getReplay?.()?.flush().catch(() => {});
  } catch {
    // The report has gone; the replay is a bonus.
  }
}

function payloadFetches(toPath: string, since: number): { count: number; lastBytes: number | null } {
  let count = 0;
  let last: PerformanceResourceTiming | null = null;
  try {
    for (const entry of performance.getEntriesByType('resource')) {
      if (!entry.name.includes('_rsc=')) continue;
      const e = entry as PerformanceResourceTiming;
      if (e.responseEnd < since) continue;
      if (new URL(e.name, window.location.href).pathname !== toPath) continue;
      count += 1;
      if (!last || e.responseEnd > last.responseEnd) last = e;
    }
  } catch {
    // Unknown is fine; the report goes either way.
  }
  return { count, lastBytes: typeof last?.encodedBodySize === 'number' ? last.encodedBodySize : null };
}

/**
 * 🚨 Not `[aria-modal="true"]` alone: the phone's menu (`MobileMenu`) is
 * mounted on every page as a permanent `aria-modal` dialog and hidden with
 * `aria-hidden`, so that selector matched it everywhere and every iPhone
 * report read `overlayOpen: true` whether anything was open or not.
 */
function overlayOpen(): boolean | null {
  try {
    return !!document.querySelector('dialog[open], [aria-modal="true"]:not([aria-hidden="true"])');
  } catch {
    return null;
  }
}

/** Called from `onRouterTransitionStart`. Never throws into the router. */
export function watchNavigation(url: string, type: NavigationType): void {
  try {
    if (typeof window === 'undefined' || reported) return;

    if (pending) {
      clearTimeout(pending.timer);
      // A RE-TAP only if the earlier one had not gone anywhere. The timer
      // outlives a navigation that committed (nothing clears it until it next
      // fires), so counting every overlap reported ordinary quick browsing as
      // re-tapping: the first event in MUCKA-WEB-BA said 2 for a user who had
      // simply left a certificate and then tapped the diary.
      const stillHere = !moved(pending);
      if (stillHere) supersededWhilePending += 1;
      // A suspect is settled here rather than dropped: one that is still
      // stuck when the user taps elsewhere is the clearest case there is.
      if (pending.suspect) settle(pending, performance.now(), stillHere ? 'superseded' : 'arrived_late');
      pending = null;
    }

    // Back/forward moves `location` before React commits, so the one signal
    // this relies on is not there to read.
    if (type === 'traverse') return;

    const target = new URL(url, window.location.href);
    if (target.origin !== window.location.origin) return;
    const to = `${target.pathname}${target.search}`;
    const from = here();
    if (to === from) return;

    const p: Pending = {
      from,
      historyState: historyState(),
      to,
      toPath: target.pathname,
      type,
      startedAt: performance.now(),
      timer: undefined as unknown as ReturnType<typeof setTimeout>,
      maxTimerLagMs: 0,
      suspect: null,
    };
    pending = p;
    arm(p, STUCK_AFTER_MS);
  } catch {
    // An instrument must never be able to break the thing it watches.
  }
}

/** Test seam: forget everything between cases. */
export function resetStuckNavigationWatch(): void {
  if (pending) clearTimeout(pending.timer);
  pending = null;
  removeHiddenListener?.();
  removeHiddenListener = null;
  reported = false;
  supersededWhilePending = 0;
  try {
    window.sessionStorage.removeItem(RECOVERY_KEY);
  } catch {
    // Nothing to forget.
  }
}
